import { useState } from 'react'
import { ImageOff } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export interface ImageWithFallbackProps {
  src: string | null | undefined
  alt: string
  /** Applied to the image AND to the fallback, so a missing image occupies the
   *  same box as a present one and the layout does not shift. */
  className?: string
  /** Icon size for the fallback. Thumbnails want a small glyph, the lightbox a
   *  large one. */
  iconClassName?: string
  /** Shown under the icon in the fallback. Omit for small thumbnails, where
   *  there is no room for words. */
  showLabel?: boolean
  onClick?: (e: React.MouseEvent) => void
}

/**
 * An image that degrades to a placeholder instead of a broken-image icon.
 *
 * Slip and parcel photos live in object storage, and a key can outlive its blob
 * — a bucket the environment cannot reach, a deleted object, a record copied
 * between environments. The browser's default for that is a torn-page glyph and
 * the alt text, which looks like a rendering bug rather than missing evidence.
 *
 * Renders the same box either way so a table row keeps its shape.
 */
export function ImageWithFallback({
  src,
  alt,
  className = '',
  iconClassName = 'w-4 h-4',
  showLabel = false,
  onClick,
}: ImageWithFallbackProps) {
  const { t } = useTranslation('common')
  // Remember WHICH src failed rather than a bare boolean. A new src then gets a
  // fresh attempt for free — paging a lightbox past one missing image must not
  // show the placeholder for every image after it — and no effect is needed to
  // reset the flag, which would cascade a second render on every load.
  const [failedSrc, setFailedSrc] = useState<string | null>(null)
  const failed = !!src && failedSrc === src

  if (!src || failed) {
    return (
      <div
        className={`flex flex-col items-center justify-center gap-1 bg-base-200 text-base-content/40 ${className}`}
        role="img"
        aria-label={t('labels.image_unavailable')}
        onClick={onClick}
      >
        <ImageOff className={iconClassName} aria-hidden="true" />
        {showLabel && <span className="text-xs">{t('labels.image_unavailable')}</span>}
      </div>
    )
  }

  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className={className}
      onError={() => setFailedSrc(src)}
      onClick={onClick}
    />
  )
}
